import React, { useState, useEffect } from 'react';
import { Heart, Users, Clock, Sparkles, Trophy, Star, Zap } from 'lucide-react';

// Real-time User Counter
export const LiveUserCounter = () => {
  const [userCount, setUserCount] = useState(127);
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      // Simulate real-time user count changes
      setUserCount(prev => {
        const change = Math.floor(Math.random() * 6) - 2; // -2 to +3
        return Math.max(50, Math.min(500, prev + change));
      });
      setIsActive(false);
      setTimeout(() => setIsActive(true), 100);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex items-center space-x-1 sm:space-x-2 bg-gradient-to-r from-green-500/10 to-emerald-500/10 backdrop-blur-md px-2 sm:px-4 py-1 sm:py-2 rounded-full border border-green-500/20 animate-fadeInUp">
      <div className={`w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-400 rounded-full ${isActive ? 'animate-pulse' : ''}`}></div>
      <Users className="w-3 h-3 sm:w-4 sm:h-4 text-green-400" />
      <span className="text-green-400 font-semibold text-xs sm:text-sm">
        <span className={`${!isActive ? 'animate-bounce' : ''}`}>{userCount}</span> users online
      </span>
    </div>
  );
};

// Achievement Badge
export const AchievementBadge = ({ icon, title, description, unlocked = false, progress = 0 }) => {
  return (
    <div className={`p-4 rounded-xl border-2 transition-all duration-300 ${
      unlocked 
        ? 'bg-gradient-to-br from-yellow-50 to-amber-50 border-yellow-300 hover-lift' 
        : 'bg-gray-50 border-gray-200 opacity-60'
    }`}>
      <div className="flex items-center space-x-3">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
          unlocked 
            ? 'bg-gradient-to-br from-yellow-400 to-amber-500 text-white animate-glow' 
            : 'bg-gray-300 text-gray-500'
        }`}>
          {icon}
        </div>
        <div className="flex-1">
          <h4 className={`font-semibold ${unlocked ? 'text-gray-800' : 'text-gray-500'}`}>
            {title}
          </h4>
          <p className="text-sm text-gray-600">{description}</p>
          {!unlocked && progress > 0 && (
            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
              <div 
                className="bg-gradient-to-r from-rose-500 to-pink-500 h-2 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Stats Card
export const StatsCard = ({ icon, label, value, trend = null, color = "rose" }) => {
  const colorClasses = {
    rose: "from-rose-500 to-pink-500",
    blue: "from-blue-500 to-cyan-500",
    green: "from-green-500 to-emerald-500",
    purple: "from-purple-500 to-violet-500"
  };

  return (
    <div className="bg-white rounded-xl p-3 sm:p-6 shadow-lg hover-lift animate-scaleIn">
      <div className="flex items-start justify-between space-x-3">
        <div className="flex-1">
          <p className="text-gray-600 text-xs sm:text-sm font-medium leading-tight">{label}</p>
          <p className="text-lg sm:text-2xl font-bold text-gray-800 mt-1">{value}</p>
          {trend && (
            <div className={`flex items-center space-x-1 mt-1 ${
              trend.direction === 'up' ? 'text-green-500' : 'text-red-500'
            }`}>
              <span className="text-xs">
                {trend.direction === 'up' ? '↗' : '↘'} {trend.value}%
              </span>
            </div>
          )}
        </div>
        <div className={`w-8 h-8 sm:w-12 sm:h-12 flex-shrink-0 rounded-full bg-gradient-to-br ${colorClasses[color]} flex items-center justify-center text-white`}>
          {icon}
        </div>
      </div>
    </div>
  );
};

// Interactive Rating Stars
export const InteractiveRating = ({ rating = 0, onRate, size = "md", readonly = false }) => {
  const [hoverRating, setHoverRating] = useState(0);
  const [currentRating, setCurrentRating] = useState(rating);

  const sizeClasses = {
    sm: "w-4 h-4",
    md: "w-6 h-6",
    lg: "w-8 h-8"
  };

  const handleRate = (newRating) => {
    if (!readonly) {
      setCurrentRating(newRating);
      onRate && onRate(newRating);
    }
  };

  return (
    <div className="flex items-center space-x-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          className={`${sizeClasses[size]} transition-all duration-200 ${
            readonly ? 'cursor-default' : 'cursor-pointer hover:scale-110'
          }`}
          onClick={() => handleRate(star)}
          onMouseEnter={() => !readonly && setHoverRating(star)}
          onMouseLeave={() => !readonly && setHoverRating(0)}
          disabled={readonly}
        >
          <Star 
            className={`w-full h-full ${
              star <= (hoverRating || currentRating) 
                ? 'text-yellow-400 fill-current animate-pulse' 
                : 'text-gray-300'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

// Gamification Progress Ring
export const ProgressRing = ({ progress = 0, size = 120, strokeWidth = 8, color = "rose" }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  const colorClasses = {
    rose: "#ec4899",
    blue: "#3b82f6",
    green: "#10b981",
    purple: "#8b5cf6"
  };

  return (
    <div className="relative flex items-center justify-center animate-scaleIn">
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#e5e7eb"
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={colorClasses[color]}
          strokeWidth={strokeWidth}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className="transition-all duration-1000 ease-out"
          style={{
          }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-2xl font-bold text-gray-700">{Math.round(progress)}%</span>
      </div>
    </div>
  );
};

// Notification Toast
export const NotificationToast = ({ message, type = "info", duration = 5000, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose && onClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const typeStyles = {
    success: "bg-green-500 text-white",
    error: "bg-red-500 text-white",
    warning: "bg-yellow-500 text-white",
    info: "bg-blue-500 text-white"
  };

  const icons = {
    success: <Heart className="w-5 h-5" />,
    error: <Zap className="w-5 h-5" />,
    warning: <Clock className="w-5 h-5" />,
    info: <Sparkles className="w-5 h-5" />
  };

  return (
    <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center space-x-3 animate-slideInRight ${typeStyles[type]}`}>
      {icons[type]}
      <span className="font-medium">{message}</span>
      <button 
        onClick={onClose}
        className="ml-2 text-white hover:text-gray-200"
      >
        ×
      </button>
    </div>
  );
};

// Social Proof Testimonial
export const TestimonialCard = ({ name, age, photo, testimonial, rating, verified = false }) => {
  return (
    <div className="bg-white rounded-xl p-6 shadow-lg hover-lift animate-fadeInUp max-w-md">
      <div className="flex items-center space-x-4 mb-4">
        <div className="relative">
          <img 
            src={photo || `https://ui-avatars.com/api/?name=${name}&background=ec4899&color=fff`}
            alt={name}
            className="w-12 h-12 rounded-full object-cover"
          />
          {verified && (
            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
              <span className="text-white text-xs">✓</span>
            </div>
          )}
        </div>
        <div>
          <h4 className="font-semibold text-gray-800">{name}, {age}</h4>
          <InteractiveRating rating={rating} readonly size="sm" />
        </div>
      </div>
      <p className="text-gray-600 italic">"{testimonial}"</p>
      <div className="mt-4 flex items-center justify-between">
        <span className="text-sm text-gray-500">Verified User</span>
        <Heart className="w-5 h-5 text-rose-500 animate-heartbeat" />
      </div>
    </div>
  );
};

export default {
  LiveUserCounter,
  AchievementBadge,
  StatsCard,
  InteractiveRating,
  ProgressRing,
  NotificationToast,
  TestimonialCard
};